import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPencilAlt, faPlus } from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import Label from "../../Utils/FormInputs/Label";
import PharmacySnackbar from "../../Utils/Snackbars/SnackbarUtil";
import DeleteDialog from "../../Utils/DeleteDialog";
import { titleCase } from "../../Utils/titleCaseFunction";
import ReusableDashboardHeader from "../../Containers/Dasboard/ReusableDashboardHeader";
import { useManageMedicine } from "./CustomHooks/useManageMedicine";
import { useExcelReports, usePdfReports } from "../Invoice/CustomHooks/useExcelReports";
import { AutoCompleteControlled } from "../../Utils/FormInputs/AutoCompleteInput";
import Tooltip from "@material-ui/core/Tooltip";
import { LoadingGif } from "../../Utils/Loader";
import * as snackbarActions from "../../Redux/Actions/SnackbarActions/SnackbarActions";

// Helper: Format number with commas
const formatNumber = (num) => {
  if (num === null || num === undefined || num === "") return "-";
  return Number(num).toLocaleString("en-US");
};

const ManageMedicine = ({ actions, snackbars }) => {
  const {
    openDialog,
    products,
    searchValue,
    handleChange,
    handleOpenDialog,
    handleCloseDialog,
    handleCloseBar,
    handlePageChange,
    handleDelete,
    isLoading,
    isSuccess,
    isError,
    product_categories,
    handleClickProductCategories,
    categoryList,
    activePage,
    totalPages,
  } = useManageMedicine(actions);

  const currentObj = { file_name: "Products", url: "Cproduct/product_list_report" };
  const currentObj_Pdf = { file_name: "Products PDF", url: "Cproduct/product_list_report_pdf" };
  const { exportExcel } = useExcelReports(actions, currentObj);
  const { exportPDF } = usePdfReports(actions, currentObj_Pdf);

  const user = JSON.parse(sessionStorage.getItem("user")) || {};
  const permissions = user.permission || {};
  const manage_medicine = permissions.manage_medicine || {};

  const headData = [
    { id: "name", label: "Product Name" },
    { id: "generic", label: "Generic Name" },
    { id: "category", label: "Category" },
    { id: "unit", label: "Unit" },
    { id: "shelf", label: "Shelf" },
    { id: "strength", label: "Strength" },
    { id: "stock", label: "Stock" },
    { id: "sellprice", label: "Selling Price" },
    { id: "wholesale_price", label: "Wholesale Price" },
    manage_medicine.create || manage_medicine.update || manage_medicine.delete
      ? { id: "action", label: "Actions" }
      : { id: "nonaction", label: "" },
  ];

  // Brand Color: #107c8e (Teal/Cyan)
  const brandColor = "#107c8e";
  const lightBg = "#e8f4f8";
  const hoverBg = "#d0edf4";
  const borderColor = "#a0d9e4";

  const styles = {
    container: { padding: "24px", backgroundColor: "#f5fafa", minHeight: "100vh" },
    card: {
      background: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 10px 30px rgba(16, 124, 142, 0.12)",
      overflow: "hidden",
      marginTop: "20px",
      border: `1px solid ${borderColor}`,
    },
    header: {
      background: `linear-gradient(135deg, ${brandColor} 0%, #0e95a8 100%)`,
      color: "white",
      padding: "20px 28px",
      fontSize: "1.4rem",
      fontWeight: "700",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    actionsBar: {
      display: "flex",
      gap: "14px",
      alignItems: "center",
      flexWrap: "wrap",
    },
    btnPrimary: {
      backgroundColor: "rgba(255,255,255,0.25)",
      color: "white",
      border: "1px solid rgba(255,255,255,0.4)",
      padding: "10px 16px",
      borderRadius: "10px",
      fontSize: "0.95rem",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s",
      backdropFilter: "blur(6px)",
    },
    searchBox: {
      padding: "12px 18px",
      borderRadius: "12px",
      border: "none",
      backgroundColor: "rgba(255,255,255,0.95)",
      color: "#222",
      fontSize: "1rem",
      width: "260px",
      boxShadow: "inset 0 2px 10px rgba(0,0,0,0.08)",
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
    },
    th: {
      backgroundColor: lightBg,
      padding: "18px 16px",
      textAlign: "left",
      fontWeight: "700",
      color: brandColor,
      fontSize: "0.85rem",
      borderBottom: `3px solid ${brandColor}`,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
    },
    td: {
      padding: "18px 16px",
      borderBottom: `1px solid ${borderColor}`,
      fontSize: "1rem",
      color: "#2d3436",
    },
    trHover: {
      transition: "all 0.3s ease",
    },
    link: {
      color: brandColor,
      fontWeight: "600",
      textDecoration: "none",
      fontSize: "1.05rem",
    },
    btnAdd: {
      background: `linear-gradient(135deg, ${brandColor}, #0e95a8)`,
      color: "white",
      border: "none",
      padding: "14px 28px",
      borderRadius: "12px",
      fontSize: "1.1rem",
      fontWeight: "600",
      cursor: "pointer",
      boxShadow: `0 8px 20px rgba(16, 124, 142, 0.3)`,
      display: "inline-flex",
      alignItems: "center",
      gap: "10px",
      transition: "all 0.3s",
    },
    btnEdit: {
      backgroundColor: "#0984e3",
      color: "white",
      padding: "10px 14px",
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
      fontSize: "1.1rem",
    },
    btnDelete: {
      backgroundColor: "#e74c3c",
      color: "white",
      padding: "10px 14px",
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
      fontSize: "1.1rem",
    },
    pagination: {
      padding: "24px",
      display: "flex",
      justifyContent: "center",
      gap: "10px",
      backgroundColor: lightBg,
      borderTop: `1px solid ${borderColor}`,
    },
    pageBtn: {
      padding: "12px 18px",
      minWidth: "48px",
      borderRadius: "12px",
      border: `2px solid ${brandColor}`,
      backgroundColor: "white",
      color: brandColor,
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
    },
    activePage: {
      backgroundColor: brandColor,
      color: "white",
      boxShadow: `0 4px 12px rgba(16,124,142,0.4)`,
    },
    emptyText: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#636e72",
      fontSize: "1.3rem",
      fontStyle: "italic",
    },
  };

  return (
    <div style={styles.container}>
      <ReusableDashboardHeader
        component="Manage Products"
        heading="Products"
        subHeading="Manage Products"
        link="/managemedicine"
      />

      <DeleteDialog openDialog={openDialog} handleDelete={handleDelete} handleClose={handleCloseDialog} message="Product" />
      <PharmacySnackbar open={snackbars.open} message={snackbars.message} variant={snackbars.variant} handleCloseBar={handleCloseBar} />

      {/* Top Controls */}
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <Label name="Filter by Category:" style={{ fontWeight: "600", color: brandColor }} />
          <AutoCompleteControlled
            options={categoryList}
            handleChange={handleClickProductCategories}
            medicine_value={product_categories}
            label_text="category_name"
            value_text="category_id"
            placeholder="All Categories"
          />
        </div>

        {manage_medicine.create && (
          <Link to="/addmedicine">
            <button
              style={styles.btnAdd}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-3px)";
                e.target.style.boxShadow = "0 12px 28px rgba(16,124,142,0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 8px 20px rgba(16,124,142,0.3)";
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Add New Product
            </button>
          </Link>
        )}
      </div>

      {/* Main Card */}
      <div style={styles.card}>
        <div style={styles.header}>
          <span>Product List</span>
          <div style={styles.actionsBar}>
            <button style={styles.btnPrimary} onClick={exportExcel}>Excel</button>
            <button style={styles.btnPrimary} onClick={exportPDF}>PDF</button>
            <input
              type="text"
              value={searchValue ? titleCase(searchValue) : ""}
              onChange={handleChange}
              placeholder="Search products..."
              style={styles.searchBox}
            />
          </div>
        </div>

        <table style={styles.table}>
          <thead>
            <tr>
              {headData.map((head) => (
                <th key={head.id} style={styles.th}>{head.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={headData.length} style={{ textAlign: "center", padding: "80px" }}>
                  <LoadingGif />
                </td>
              </tr>
            )}

            {isSuccess && products.length === 0 && (
              <tr>
                <td colSpan={headData.length} style={styles.emptyText}>
                  No products found. Try adjusting your filters.
                </td>
              </tr>
            )}

            {isSuccess &&
              products.map((product, idx) => (
                <tr
                  key={product.product_id || idx}
                  style={styles.trHover}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                >
                  <td style={styles.td}>
                    <Link to={`/medicinedetails/${product.product_id}`} style={styles.link}>
                      {titleCase(product.product_name)}
                    </Link>
                  </td>
                  <td style={styles.td}>{titleCase(product.generic_name || "-")}</td>
                  <td style={styles.td}>{product.category_name || "-"}</td>
                  <td style={styles.td}>{product.unit || "-"}</td>
                  <td style={styles.td}>{product.product_location || "-"}</td>
                  <td style={styles.td}>{product.strength || "-"}</td>
                  <td style={{ ...styles.td, fontWeight: "700", color: product.stock < 10 ? "#e74c3c" : brandColor }}>
                    {formatNumber(product.stock)}
                  </td>
                  <td style={{ ...styles.td, fontWeight: "600", color: brandColor }}>
                    {product.price_type === 1 ? `${product.price}%` : formatNumber(product.price) || "-"}
                  </td>
                  <td style={{ ...styles.td, fontWeight: "600" }}>
                    {formatNumber(product.wholesale_price) || "-"}
                  </td>

                  {(manage_medicine.update || manage_medicine.delete) && (
                    <td style={{ ...styles.td, whiteSpace: "nowrap", gap: "8px", display: "flex" }}>
                      {manage_medicine.update && (
                        <Tooltip title="Edit">
                          <Link to={`/editmedicine/${product.product_id}`}>
                            <button style={styles.btnEdit}>
                              <FontAwesomeIcon icon={faPencilAlt} />
                            </button>
                          </Link>
                        </Tooltip>
                      )}
                      {manage_medicine.delete && (
                        <Tooltip title="Delete">
                          <button style={styles.btnDelete} onClick={() => handleOpenDialog(product.product_id)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </Tooltip>
                      )}
                    </td>
                  )}
                </tr>
              ))}

            {isError && (
              <tr>
                <td colSpan={headData.length} style={{ ...styles.emptyText, color: "#e74c3c" }}>
                  Failed to load products. Please refresh the page.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => handlePageChange(i + 1)}
                style={{
                  ...styles.pageBtn,
                  ...(activePage === i + 1 ? styles.activePage : {}),
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  snackbars: state.snackbars,
});

const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(mapStateToProps, mapDispatchToProps)(ManageMedicine);